import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { create } from 'zustand';
import { EKD, EKINDS, fmt, fmtDate, heat, pathName, sgn } from '@balansa/shared';
import { strDots } from '../lib/helpers';
import { useApp, useWheel } from '../store';

interface TipState {
  key: string | null;
  custom: ReactNode | null;
  x: number;
  y: number;
  show(key: string, e: { clientX: number; clientY: number }): void;
  showCustom(node: ReactNode, e: { clientX: number; clientY: number }): void;
  hide(): void;
}

/** Atsevišķs mazs stāvoklis, lai peles kustība pārzīmē tikai uzvedni, nevis visu apli. */
export const useTip = create<TipState>()((set, get) => ({
  key: null,
  custom: null,
  x: 0,
  y: 0,
  show(key, e) {
    set({ key, custom: null, x: e.clientX, y: e.clientY });
  },
  showCustom(custom, e) {
    set({ key: 'custom', custom, x: e.clientX, y: e.clientY });
  },
  hide() {
    if (get().key) set({ key: null, custom: null });
  }
}));

export function Tooltip() {
  const { key, custom, x, y } = useTip();
  const ref = useRef<HTMLDivElement>(null);
  const content = useTipContent(key, custom);

  useLayoutEffect(() => {
    const tip = ref.current;
    if (!tip || !key || !content) return;
    const wrap = tip.parentElement!.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let px = x - wrap.left + 16;
    let py = y - wrap.top + 16;
    if (px + tw > wrap.width) px = x - wrap.left - tw - 16;
    if (py + th > wrap.height) py = y - wrap.top - th - 12;
    tip.style.transform = `translate(${Math.max(4, px)}px,${Math.max(4, py)}px)`;
  });

  return (
    <div className={'tip' + (key && content ? ' on' : '')} ref={ref} role="tooltip" data-testid="tip">
      {key ? content : null}
    </div>
  );
}

function useTipContent(key: string | null, custom: ReactNode | null): ReactNode {
  const { wheel, idx, en } = useWheel();
  const mode = useApp((s) => s.ui.mode);
  const linkSrc = useApp((s) => s.ui.linkSrc);
  return useMemo(() => {
    if (!key) return null;
    if (key === 'custom') return custom;
    const i = key.indexOf(':');
    const kind = key.slice(0, i);
    const id = key.slice(i + 1);
    if (kind === 'link') {
      const l = wheel.links.find((x) => x.id === id);
      if (!l || !idx.node.has(l.fromId) || !idx.node.has(l.toId)) return null;
      return (
        <>
          <div className="tt-h">
            {idx.node.get(l.fromId)!.name} → {idx.node.get(l.toId)!.name}
          </div>
          <div className={'tt-f ' + l.type}>
            <em style={{ margin: '4px 0 0', padding: 0 }}>
              {l.type === 'give' ? 'dod' : 'atņem'}
              {l.label ? ' ' + l.label : ''}
            </em>
          </div>
          <div className="tt-hint">Stiprums {strDots(l.strength)} · klikšķini, lai labotu</div>
        </>
      );
    }
    if (kind !== 'node' || !idx.node.has(id)) return null;
    const n = idx.node.get(id)!;
    const v = idx.val.get(id)!;
    const kids = idx.kids.get(id)!;
    const th = wheel.settings.threshold;
    const cmp = wheel.settings.compare ? wheel.snapshots.find((s) => s.id === wheel.settings.compare) : null;
    let delta: ReactNode = null;
    if (cmp && cmp.vals[id] != null) {
      const d = v - cmp.vals[id];
      delta =
        Math.abs(d) >= 0.05 ? (
          <span className={'tt-d ' + (d > 0 ? 'up' : 'down')}>
            {(d > 0 ? '▲ ' : '▼ ') + sgn(d)} kopš {fmtDate(cmp.ts)}
          </span>
        ) : (
          <span className="tt-d">bez izmaiņām</span>
        );
    }
    const inSub = (x: string) => (idx.anc.get(x) || []).includes(id);
    const out = wheel.links.filter((l) => inSub(l.fromId) && !inSub(l.toId) && idx.node.has(l.toId));
    const inc = wheel.links.filter((l) => inSub(l.toId) && !inSub(l.fromId) && idx.node.has(l.fromId));
    const row = (l: (typeof out)[number], dir: 'out' | 'in') => (
      <div className={'tt-f ' + l.type} key={l.id + dir}>
        <span>{dir === 'out' ? '→' : '←'}</span>
        {idx.node.get(dir === 'out' ? l.toId : l.fromId)!.name}
        <em>
          {l.type === 'give' ? 'dod' : 'atņem'}
          {l.label ? ' ' + l.label : ''}
        </em>
      </div>
    );
    const el: ReactNode[] = [];
    const er = en.M.rows.find((x) => x.id === id);
    if (er)
      el.push(
        er.role === 'shield' ? (
          <div className="tt-e sh" key="e">
            Noturība: −{fmt(er.eff)}% {EKD[er.kind!]}
          </div>
        ) : (
          <div className={'tt-e ' + (er.eff >= 0 ? 'pos' : 'neg')} key="e">
            {er.role === 'sleep' ? 'Miegs (naktī)' : er.role === 'food' ? 'Ēdiens' : er.role === 'charge' ? 'Uzlādē dienā' : 'Patērē'}: {sgn(er.eff)} v.
            {er.role === 'drain' && er.kind && er.kind !== 'all' ? ' · ' + EKINDS[er.kind] + ' slodze' : ''}
          </div>
        )
      );
    const cr = en.M.capRows.find((x) => x.id === id);
    if (cr)
      el.push(
        <div className={'tt-e ' + (cr.units > 0 ? 'pos' : cr.units < 0 ? 'neg' : '')} key="cap">
          Ietilpība: {cr.units > 0 ? '+' : cr.units < 0 ? '−' : ''}
          {Math.abs(cr.units)} v.{cr.min != null ? ' (līmenis no ' + fmt(cr.min) + ')' : ''}
        </div>
      );
    if (kids.length) {
      const sub = en.M.rows.filter((x) => x.role !== 'shield' && x.id !== id && inSub(x.id));
      if (sub.length) {
        const t = sub.reduce((a, x) => a + x.eff, 0);
        el.push(
          <div className={'tt-e ' + (t >= 0 ? 'pos' : 'neg')} key="sum">
            Kopā jomā: {sgn(t)} v. diennaktī
          </div>
        );
      }
    }
    return (
      <>
        <div className="tt-h">
          <i style={{ background: idx.color(id) }} />
          {pathName(idx, id)}
        </div>
        <div className="tt-v">
          <b>{fmt(v)}</b>
          <small>/10</small>
          {v <= th ? <span className="tt-low">trūkums</span> : null}
          {delta}
        </div>
        {kids.length ? (
          <>
            <div className="tt-kids">
              {kids.map((k) => (
                <div key={k}>
                  <span>{idx.node.get(k)!.name}</span>
                  <b style={{ color: heat(idx.val.get(k)!) }}>{fmt(idx.val.get(k)!)}</b>
                </div>
              ))}
            </div>
            <div className="tt-hint">Vērtība = apakšjomu vidējais</div>
          </>
        ) : null}
        {out.length ? (
          <>
            <div className="tt-sec">Uz āru</div>
            {out.map((l) => row(l, 'out'))}
          </>
        ) : null}
        {inc.length ? (
          <>
            <div className="tt-sec">Iekšā</div>
            {inc.map((l) => row(l, 'in'))}
          </>
        ) : null}
        {el.length ? (
          <>
            <div className="tt-sec">Enerģija</div>
            {el}
          </>
        ) : null}
        {n.note ? <div className="tt-note">{n.note}</div> : null}
        {mode === 'link' ? (
          <div className="tt-hint">{linkSrc && linkSrc !== id ? 'Klikšķini — saite uz šo jomu' : 'Velc uz citu jomu vai klikšķini, lai sāktu saiti'}</div>
        ) : (
          <div className="tt-hint">
            {!kids.length ? (
              <>
                Velc punktu — mainīt vērtību.
                <br />
              </>
            ) : null}
            Velc no nosaukuma uz citu jomu — jauna saite
          </div>
        )}
      </>
    );
  }, [key, custom, wheel, idx, en, mode, linkSrc]);
}
